import React, { useState, useEffect } from "react";
import {
  List,
  ListItem,
  IconButton,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Modal,
  ListItemButton,
  Menu,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import {
  createNewGroup,
  deleteGroup,
  fetchBookmarkCases,
  fetchBookmarkGroups,
  fetchBookmarkStatutes,
  renameGroup,
} from "../../store/Bookmarks/bookmarksActions";

const BookmarksSidebar = ({
  toggleSidebar,
  selectedGroup,
  setSelectedGroup,
  isDeleteBookmarkLoading,
  setIsDeleteBookmarkLoading,
}) => {
  const dispatch = useDispatch();

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const bookmarkGroups = useSelector((state) => state.bookmarkGroups);
  const { loading, groups } = bookmarkGroups;

  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState("");
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const inputRef = React.useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddBookmarkLoading, setIsAddBookmarkLoading] = useState(false);

  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);

  const handleGroupClick = async (event, group) => {
    event.stopPropagation();

    // Update selected group state first
    setSelectedGroup({ id: group._id, name: group.groupName });

    // Clear any editing states
    setEditingGroup(null);
    setMenuAnchorEl(null);

    // Fetch data for the selected group
    await dispatch(fetchBookmarkCases(group));
    await dispatch(fetchBookmarkStatutes(group));

    // Close sidebar on mobile after data is fetched
    if (isMobileView && toggleSidebar) {
      toggleSidebar();
    }
  };

  const confirmDeleteGroup = (e, groupId) => {
    e.stopPropagation();
    setGroupToDelete(groupId);
    setConfirmationModalOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteGroup = async () => {
    const groupId = groupToDelete;
    const groupName = groups.find((group) => group._id === groupId).groupName;

    if (groupName === "Bookmarks") return;

    try {
      setIsDeleteBookmarkLoading(true);
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal());
        setIsDeleteBookmarkLoading(false);
        return;
      }
      // Wait for the delete operation to complete
      await dispatch(deleteGroup(userInfo?._id, groupId));
      // Refetch groups to update UI immediately after deletion
      dispatch(fetchBookmarkGroups(userInfo?._id));
      // Select first available group (excluding deleted one)
      const remainingGroups = groups.filter((group) => group._id !== groupId);
      if (remainingGroups.length > 0) {
        setSelectedGroup({
          id: remainingGroups[0]?._id,
          name: remainingGroups[0]?.groupName,
        });
        dispatch(fetchBookmarkCases(remainingGroups[0]));
        dispatch(fetchBookmarkStatutes(remainingGroups[0]));
      }
      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Delete Bookmark Group" },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error deleting group:", error);
    } finally {
      setConfirmationModalOpen(false);
      setIsDeleteBookmarkLoading(false);
    }
  };

  const handleRenameGroup = async (e, groupId) => {
    e.stopPropagation();
    try {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal());
        return;
      }
      dispatch(renameGroup(userInfo?._id, groupId, newGroupName));
      setEditingGroup({ groupId: "", index: -1 });
      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Rename bookmark group title" },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error renaming the group:", error);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupNameInput) return;

    try {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal());
        return;
      }
      setIsAddBookmarkLoading(true);

      // Wait for the group creation to complete
      await dispatch(createNewGroup(userInfo._id, groupNameInput));

      setModalOpen(false);
      setGroupNameInput("");

      // Deduct credits using the helper
      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Create Bookmark Group" },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error creating group:", error);
    } finally {
      setIsAddBookmarkLoading(false);
    }
  };

  const handleMenuOpen = (event, groupId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleCancelEdit = (event) => {
    event.stopPropagation();
    setNewGroupName("");
    setEditingGroup(null);
  };

  const handleEditTitle = (event, groupId, groupName) => {
    event.stopPropagation();
    setEditingGroup(groupId);
    setNewGroupName(groupName);
    setMenuAnchorEl(null);
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current.selectionStart;
    setNewGroupName(e.target.value);

    // Set cursor back to where it was
    setTimeout(() => {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredGroups = groups?.filter((group) =>
    group.groupName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    dispatch(fetchBookmarkGroups(userInfo._id, true));
  }, []);

  useEffect(() => {
    if (groups?.length > 0 && Object.keys(selectedGroup)?.length === 0) {
      setSelectedGroup({ id: groups[0]?._id, name: groups[0]?.groupName });
    }
  }, [groups, selectedGroup, dispatch]);

  useEffect(() => {
    setMenuAnchorEl(null);
  }, [selectedGroup]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: "278px",
        boxSizing: "border-box",
        margin: "12px",
        backgroundColor: "#F8FAFC",
        borderRadius: "12px",
        border: "1px solid #F1F5F9",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          p: "10px",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
          disabled={isAddBookmarkLoading}
          sx={{
            backgroundColor: "#003D78",
            textTransform: "none",
            fontWeight: "bold",
            marginTop: "8px",
            marginBottom: "8px",
            "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            transition: "background-color 0.3s ease-in-out",
          }}
        >
          {isAddBookmarkLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
              Creating...
            </>
          ) : (
            "New Group"
          )}
        </Button>

        {/* Search Bar */}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search groups..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            width: "100%",
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#ccc",
              },
              "&:hover fieldset": {
                borderColor: "#00509e",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#00509e",
              },
            },
            "& input": {
              padding: "10px",
              fontSize: "13px",
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: "#303f9f" }} fontSize="small" />
            ),
          }}
        />
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : (
          <List
            sx={{
              overflowY: "auto",
              "::-webkit-scrollbar": {
                width: "5px",
              },
              "::-webkit-scrollbar-track": {
                background: "#fff",
              },
              "::-webkit-scrollbar-thumb": {
                background: "#bbb",
                borderRadius: "6px",
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                padding: "4px 16px 0px 12px",
                color: "#3C50B1",
                fontWeight: "bold",
                textTransform: "none",
              }}
            >
              Bookmark Groups
            </Typography>
            {filteredGroups?.map((group, index) => (
              <ListItem key={group._id} disablePadding>
                <ListItemButton
                  disableRipple
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "38px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    mr: 1,
                    "&.Mui-selected": {
                      backgroundColor: "#e8eefc",
                      "&:hover": {
                        backgroundColor: "#e8eefc",
                      },
                    },
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#e8eefc",
                    },
                  }}
                  selected={
                    selectedGroup.id === group._id || editingGroup === group._id
                  }
                  onClick={(e) => handleGroupClick(e, group)}
                >
                  {editingGroup === group._id ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <input
                        ref={inputRef}
                        style={{
                          width: "100%",
                          padding: "4px",
                          marginLeft: "0",
                          fontSize: "13px",
                        }}
                        type="text"
                        autoFocus
                        value={newGroupName}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onChange={handleInputChange}
                      />
                      <IconButton
                        edge="end"
                        onClick={(e) => handleRenameGroup(e, group._id)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => handleCancelEdit(e)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Stack
                        direction="row"
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                            flexGrow: 1,
                          }}
                        >
                          {group.groupName}
                        </Typography>
                        {selectedGroup.id === group._id &&
                          selectedGroup.name !== "Bookmarks" && (
                            <>
                              <IconButton
                                size="small"
                                aria-label="options"
                                onClick={(e) => {
                                  handleMenuOpen(e, group._id);
                                }}
                              >
                                <MoreHorizIcon />
                              </IconButton>
                              <Menu
                                anchorEl={menuAnchorEl}
                                open={Boolean(menuAnchorEl)}
                                onClose={handleMenuClose}
                              >
                                <MenuItem
                                  sx={{ fontSize: "14px" }}
                                  onClick={(e) => {
                                    handleEditTitle(
                                      e,
                                      group?._id,
                                      group?.groupName,
                                    );
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16, mr: 1 }} />
                                  Edit
                                </MenuItem>
                                <MenuItem
                                  sx={{
                                    fontSize: "14px",
                                    color: "#d32f2f",
                                  }}
                                  onClick={(e) =>
                                    confirmDeleteGroup(e, group?._id)
                                  }
                                >
                                  <DeleteIcon
                                    color="error"
                                    sx={{ fontSize: 16, mr: 1 }}
                                  />
                                  Delete
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                      </Stack>
                    </>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "80vw", md: 300 },
            bgcolor: "white",
            p: { xs: 2, md: 4 },
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              color: "#303f9f",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: "bold",
            }}
          >
            Create New Group
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            label="Group Name"
            value={groupNameInput}
            onChange={(e) => setGroupNameInput(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "13px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                  WebkitTextFillColor: "black !important",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: "13px",
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={isAddBookmarkLoading}
            startIcon={
              isAddBookmarkLoading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : null
            }
            sx={{
              mt: 2,
              width: "100%",
              textTransform: "none",
              backgroundColor: "#003D78",
              "&:hover": { backgroundColor: "primary.main", color: "#fff" },
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            {isAddBookmarkLoading ? "Creating..." : "Create"}
          </Button>
        </Box>
      </Modal>

      {/* Global Loader Overlay */}
      {isAddBookmarkLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              padding: 3,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#003D78" }} />
            <Typography variant="h6" sx={{ color: "#003D78", fontWeight: 600 }}>
              Creating Group...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please wait while we create your bookmark group.
            </Typography>
          </Box>
        </Box>
      )}

      <Modal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "85vw", md: 400 },
            bgcolor: "white",
            p: { xs: 1, md: 3 },
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "16px",
          }}
        >
          {/* Modal Title */}
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-start" },
              fontSize: { xs: "16px", md: "18px" },
              color: "#303f9f",
              fontWeight: "bold",
              borderBottom: "1px solid #e0e0e0",
              pb: { xs: 1, md: 2 },
            }}
          >
          Confirm Deletion
          </Typography>

          {/* Modal Content */}
          <Typography
            variant="body1"
            textAlign="center"
            sx={{
              mt: { xs: 1, md: 3 },
              mb: 1,
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            Are you sure you want to delete this group?
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            sx={{ mb: 3, fontSize: { xs: "13px", md: "14px" } }}
          >
            This action cannot be undone.
          </Typography>

          {/* Modal Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: 2,
            }}
          >
            <Button
              onClick={() => setConfirmationModalOpen(false)}
              color="primary"
              variant="outlined"
              sx={{
                borderRadius: "24px",
                fontSize: { xs: "13px", lg: "14px" },
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteGroup}
              color="error"
              variant="contained"
              sx={{
                borderRadius: "24px",
                fontSize: { xs: "13px", lg: "14px" },
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: "bold",
                ml: { xs: 1, md: 2 },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default BookmarksSidebar;
